import { CATEGORY_LABELS, replySchema, SERVICE_MODE_LABELS, type FeedbackInput } from '@helpdesk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Heart, MessageCircleQuestion, Send } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { FeedbackForm } from '../components/FeedbackForm.tsx';
import { OverdueBadge, PersonalBadge, PriorityBadge, StatusBadge } from '../components/ticket-badges.tsx';
import { TicketTimeline } from '../components/TicketTimeline.tsx';
import { Button } from '../components/ui/button.tsx';
import { Textarea } from '../components/ui/form.tsx';
import { BackButton } from '../components/ui/back.tsx';
import { Alert, Card, Spinner } from '../components/ui/misc.tsx';
import { AttachmentPicker, StarDisplay } from '../components/ui/pickers.tsx';
import { useToast } from '../components/ui/toast.tsx';
import { api, ApiError } from '../lib/api.ts';
import { formatDateTime, formatDuration } from '../lib/format.ts';
import { usePageTitle } from '../lib/navigation.ts';
import { useSession } from '../lib/session.ts';
import type { TicketDetail } from '../lib/types.ts';
import { validate } from '../lib/validate.ts';
import { NotFoundPage } from './NotFoundPage.tsx';

export const ticketKey = (number: string) => ['ticket', number.toUpperCase()] as const;

export function useTicket(number: string) {
  return useQuery({
    queryKey: ticketKey(number),
    queryFn: () => api.get<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });
}

/**
 * One ticket: the conversation, a reply box, rating once resolved, and a
 * details panel. `sidePanel` lets the IT workspace add its controls.
 */
/** Extra tools above the reply box, e.g. IT's saved replies; `insert` adds text to the reply. */
export type ReplyTools = (detail: TicketDetail, insert: (text: string) => void) => ReactNode;

export function TicketPage({
  sidePanel,
  replyTools,
}: {
  sidePanel?: (detail: TicketDetail) => ReactNode;
  replyTools?: ReplyTools;
}) {
  const { number = '' } = useParams();
  const { user, isIt } = useSession();
  const query = useTicket(number);
  usePageTitle(query.data?.ticket.number ?? number);

  if (query.isPending) return <Spinner />;
  if (query.error instanceof ApiError && query.error.status === 404) return <NotFoundPage what="ticket" />;
  if (query.isError) return <Alert tone="error">{query.error.message}</Alert>;

  const detail = query.data;
  const { ticket } = detail;
  const isRequester = user?.id === detail.requester.id;
  const forStaff = !isIt;

  return (
    <div>
      <div className="mb-2">
        <BackButton to={isIt ? '/queue' : '/tickets'} />
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={ticket.status} forStaff={forStaff} />
          {isIt && ticket.overdue && <OverdueBadge />}
          {isIt && ticket.personal && <PersonalBadge />}
          <span className="font-mono text-sm text-muted">{ticket.number}</span>
        </div>
        <h1 className="mt-2 text-2xl font-bold tracking-tight break-words">{ticket.subject}</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-6">
          {ticket.voided && (
            <Alert tone="info" title={isIt ? 'This ticket is voided' : 'This ticket was cancelled by IT'}>
              {isIt
                ? `${ticket.voided.by ?? 'An admin'} voided it${ticket.voided.reason ? `: “${ticket.voided.reason}”` : ''}. It is left out of the queue, the dashboard and reports.`
                : 'If you still need help, please report a new problem.'}
            </Alert>
          )}
          {isRequester && ticket.status === 'waiting' && (
            <Alert tone="warning" title="IT needs more information">
              Please read the latest message and reply below — your ticket is on hold until you do.
            </Alert>
          )}

          <TicketTimeline detail={detail} forStaff={forStaff} />

          {detail.canRate && <RateCard number={ticket.number} />}
          {detail.feedback && <FeedbackSummary feedback={detail.feedback} />}

          {detail.canReply ? (
            <ReplyBox detail={detail} isIt={isIt} tools={replyTools} />
          ) : (
            <p className="rounded-lg bg-surface-2 p-4 text-sm text-muted">
              This ticket is {ticket.status === 'void' ? 'cancelled' : 'closed'}. If the problem has come
              back,{' '}
              <Link to="/new" className="font-medium text-brand hover:underline">
                report it as a new ticket
              </Link>
              .
            </p>
          )}
        </div>

        <aside className="space-y-4">
          {sidePanel?.(detail)}
          <DetailsCard detail={detail} showRequester={isIt} />
        </aside>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-2 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}

function DetailsCard({ detail, showRequester }: { detail: TicketDetail; showRequester: boolean }) {
  const { ticket, requester, assignee } = detail;
  return (
    <Card className="p-4">
      <h2 className="mb-1 text-sm font-semibold">Details</h2>
      <dl className="divide-y divide-line">
        {showRequester && (
          <Row label="Requester">
            <span className="block">{requester.name}</span>
            <a
              href={`mailto:${requester.email}`}
              className="block text-xs font-normal text-brand hover:underline"
            >
              {requester.email}
            </a>
          </Row>
        )}
        {ticket.phone && (
          <Row label="Phone">
            <a href={`tel:${ticket.phone}`} className="text-brand hover:underline">
              {ticket.phone}
            </a>
          </Row>
        )}
        <Row label="Priority">
          <PriorityBadge priority={ticket.priority} />
        </Row>
        <Row label="Type">{CATEGORY_LABELS[ticket.category]}</Row>
        <Row label="Department">{ticket.department}</Row>
        <Row label="Office">{ticket.officeLocation}</Row>
        <Row label="Help">{SERVICE_MODE_LABELS[ticket.serviceMode]}</Row>
        {/* IT sees (and changes) the assignee in the actions panel. */}
        {!showRequester && (
          <Row label="Handled by">
            {assignee?.name ?? <span className="font-normal text-muted">Not assigned yet</span>}
          </Row>
        )}
        <Row label="Reported">{formatDateTime(ticket.createdAt)}</Row>
        {ticket.resolvedAt && <Row label="Resolved">{formatDateTime(ticket.resolvedAt)}</Row>}
        {ticket.resolutionMinutes !== null && (
          <Row label="Time to resolve">{formatDuration(ticket.resolutionMinutes)}</Row>
        )}
      </dl>
    </Card>
  );
}

function ReplyBox({ detail, isIt, tools }: { detail: TicketDetail; isIt: boolean; tools?: ReplyTools }) {
  const client = useQueryClient();
  const toast = useToast();
  const [body, setBody] = useState('');
  const insert = (text: string) => {
    setBody((current) => (current.trim() ? `${current.trimEnd()}\n\n${text}` : text));
    requestAnimationFrame(() => document.getElementById('reply')?.focus());
  };
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>();
  const closed = detail.ticket.status === 'closed';
  const asNote = isIt && (internal || closed);

  const send = useMutation({
    mutationFn: (form: FormData) =>
      api.post<TicketDetail>(`/api/tickets/${detail.ticket.number}/replies`, form),
    onSuccess: (updated) => {
      client.setQueryData(ticketKey(detail.ticket.number), updated);
      void client.invalidateQueries({ queryKey: ['tickets'] });
      setBody('');
      setFiles([]);
      setInternal(false);
      toast({ tone: 'success', title: asNote ? 'Note added' : 'Reply sent' });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(replySchema, { body, internal: asNote });
    if (!result.ok) return setError(result.fields.body ?? 'Please write a message.');
    setError(undefined);
    const form = new FormData();
    form.set('body', result.data.body);
    form.set('internal', String(result.data.internal));
    files.forEach((f) => form.append('attachments', f));
    send.mutate(form);
  };

  return (
    <Card className={`p-4 ${asNote ? 'border-warning/50' : ''}`}>
      <form onSubmit={submit} noValidate>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 empty:hidden">
          {isIt && !closed && (
            <div
              role="tablist"
              aria-label="Message type"
              className="inline-flex rounded-lg bg-surface-2 p-1 text-sm"
            >
              {[
                [false, 'Reply to requester'],
                [true, 'Internal note'],
              ].map(([value, label]) => (
                <button
                  key={String(value)}
                  type="button"
                  role="tab"
                  aria-selected={internal === value}
                  onClick={() => setInternal(value as boolean)}
                  className={`rounded-md px-3 py-1 font-medium ${internal === value ? 'bg-surface shadow-card' : 'text-muted'}`}
                >
                  {label as string}
                </button>
              ))}
            </div>
          )}
          {tools?.(detail, insert)}
        </div>
        <label htmlFor="reply" className="sr-only">
          {asNote ? 'Internal note' : 'Your reply'}
        </label>
        <Textarea
          id="reply"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            asNote
              ? 'Only IT will see this note…'
              : isIt
                ? 'Write a reply to the requester…'
                : detail.ticket.status === 'resolved'
                  ? 'Not fixed? Reply here and the ticket goes back to IT…'
                  : 'Write a message to IT…'
          }
          rows={3}
          maxLength={5000}
          aria-invalid={!!error}
          className={asNote ? 'bg-warning-soft/40' : ''}
        />
        {error && (
          <p role="alert" className="mt-1.5 text-sm text-danger">
            {error}
          </p>
        )}
        <div className="mt-3 flex items-start justify-between gap-3">
          <AttachmentPicker files={files} onChange={setFiles} onError={setError} compact />
          <Button type="submit" loading={send.isPending} variant={asNote ? 'secondary' : 'primary'}>
            <Send className="size-4" aria-hidden />
            {asNote ? 'Add note' : 'Send'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function RateCard({ number }: { number: string }) {
  const client = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string>();
  const rate = useMutation({
    mutationFn: (input: FeedbackInput) => api.post(`/api/tickets/${number}/feedback`, input),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Thank you for your feedback!' });
      void client.invalidateQueries({ queryKey: ticketKey(number) });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });
  return (
    <Card className="p-5">
      <h2 className="flex items-center gap-2 font-semibold">
        <Heart className="size-5 text-brand" aria-hidden />
        How did we do?
      </h2>
      <p className="mt-1 mb-5 text-sm text-muted">
        Your rating takes less than a minute and helps IT improve.
      </p>
      <FeedbackForm onSubmit={(input) => rate.mutate(input)} pending={rate.isPending} serverError={error} />
    </Card>
  );
}

function FeedbackSummary({ feedback }: { feedback: NonNullable<TicketDetail['feedback']> }) {
  return (
    <Card className="p-5">
      <h2 className="flex items-center gap-2 font-semibold">
        <MessageCircleQuestion className="size-5 text-brand" aria-hidden />
        Feedback
      </h2>
      <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        {(
          [
            ['Overall', feedback.satisfaction],
            ['Response time', feedback.responseTime],
            ['Quality of fix', feedback.resolutionQuality],
            ['Professionalism', feedback.professionalism],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-2">
            <dt className="text-muted">{label}</dt>
            <dd>
              <StarDisplay value={value} />
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-sm">
        Would recommend: <strong>{feedback.wouldRecommend ? 'Yes' : 'No'}</strong>
      </p>
      {feedback.comments && (
        <p className="mt-2 rounded-lg bg-surface-2 p-3 text-sm whitespace-pre-wrap">{feedback.comments}</p>
      )}
    </Card>
  );
}
